import { AlertTriangle, Share2, Sparkles } from 'lucide-react';

import { Button } from '@/components/common/Button/Button';
import { Badge } from '@/components/common/Badge/Badge';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { SourceLink } from '@/components/common/SourceLink/SourceLink';
import type { ProposalAnswerViewModel, ProposalQuestionViewState } from '@/types/backend-contracts';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/home.module.css';

const SUMMARY_SHARE_LIMIT = 180;

type AiResponseCardProps = {
  response: ProposalQuestionViewState;
  loadingMessages: string[];
  className?: string;
  sharePath?: string;
};

function truncateText(value: string, limit: number): string {
  const normalizedValue = value.trim();

  if (normalizedValue.length <= limit) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
}

function buildResponseShareText(response: Extract<ProposalQuestionViewState, { status: 'answered' }>): string {
  const lines: string[] = ['Resposta da IA', '', 'Resumo', truncateText(response.answer.summary, SUMMARY_SHARE_LIMIT), '', 'Compartilhado via Verde Confirma'];

  return lines.join('\n').trim();
}

function buildResponseItemShareText(item: ProposalAnswerViewModel['cards'][number]): string {
  const lines: string[] = [item.title.trim(), item.description.trim()];

  if (item.sources.length > 0) {
    lines.push('', 'Fontes oficiais');

    item.sources.forEach((source) => {
      lines.push(`- ${source.label}`);
    });
  }

  lines.push('', 'Compartilhado via Verde Confirma');

  return lines.join('\n').trim();
}

function resolveShareUrl(answerId: string, sharePath = '/'): string {
  const url = new URL(sharePath.trim() || '/', window.location.href);
  url.searchParams.set('iar', answerId);

  return url.toString();
}

async function shareContent(
  response: Extract<ProposalQuestionViewState, { status: 'answered' }>,
  options: {
    title: string;
    text: string;
    sharePath?: string;
  },
): Promise<void> {
  const url = resolveShareUrl(response.answer.answerId, options.sharePath);
  const shareData = {
    title: options.title,
    text: options.text,
    url,
  };

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share(shareData);
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${options.text}\n\nLink da resposta: ${shareData.url}`);
  }
}

function shareResponse(response: Extract<ProposalQuestionViewState, { status: 'answered' }>, sharePath?: string): Promise<void> {
  return shareContent(response, {
    title: 'Resposta da IA',
    text: buildResponseShareText(response),
    sharePath,
  });
}

function shareResponseItem(
  response: Extract<ProposalQuestionViewState, { status: 'answered' }>,
  item: ProposalAnswerViewModel['cards'][number],
  sharePath?: string,
): Promise<void> {
  return shareContent(response, {
    title: item.title,
    text: buildResponseItemShareText(item),
    sharePath,
  });
}

export function AiResponseCard({ response, loadingMessages, className, sharePath = '/' }: AiResponseCardProps): JSX.Element | null {
  if (response.status === 'loading') {
    return <LoadingState title="Consultando as propostas..." messages={loadingMessages} className={className} />;
  }

  if (response.status === 'idle') {
    return null;
  }

  if (response.status === 'error') {
    return (
      <EmptyState
        className={classNames(styles.responseCard, className)}
        tone="danger"
        icon={<AlertTriangle size={20} />}
        title="Não foi possível consultar as propostas agora"
        description={response.message}
      />
    );
  }

  if (response.status === 'needs-clarification') {
    return (
      <EmptyState
        className={classNames(styles.responseCard, className)}
        tone="warning"
        icon={<AlertTriangle size={20} />}
        title="Precisamos de mais detalhes"
        description={response.message}
      />
    );
  }

  if (response.status === 'no-proposals') {
    return (
      <EmptyState
        className={classNames(styles.responseCard, className)}
        icon={<Sparkles size={20} />}
        title="Nenhuma proposta localizada"
        description={response.message}
      />
    );
  }

  return (
    <Card tone="soft" className={classNames(styles.responseCard, className)}>
      <div className={styles.responseHeader}>
        <Badge tone="neutral">Resposta da IA</Badge>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          ariaLabel="Compartilhar resposta da IA"
          onClick={() => {
            void shareResponse(response, sharePath);
          }}
        >
          <Share2 size={14} />
          Compartilhar
        </Button>
      </div>

      <div>
        <p className={styles.responseSummary}>{response.answer.summary}</p>
      </div>

      <div className={styles.responseItems}>
        {response.answer.cards.map((card, index) => (
          <article key={`${index}-${card.title}`} className={styles.responseItem}>
            <div className={styles.responseItemHeader}>
              <div className={styles.responseItemCopy}>
                <p className={styles.responseItemTitle}>{card.title}</p>
                <p className={styles.responseItemDescription}>{card.description}</p>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                ariaLabel={`Compartilhar item ${card.title}`}
                className={styles.responseItemShareButton}
                onClick={() => {
                  void shareResponseItem(response, card, sharePath);
                }}
              >
                <Share2 size={14} />
                Compartilhar item
              </Button>
            </div>

            <div className={styles.responseSources} aria-label="Fontes oficiais">
              {card.sources.map((source) => (
                <SourceLink key={`${source.label}-${source.url}`} href={source.url} label={source.label} external />
              ))}
            </div>
          </article>
        ))}
      </div>

      {response.answer.limitations.length > 0 ? (
        <div>
          <p className={styles.responseLimitationsTitle}>Limitações</p>
          <ul className={styles.responseLimitations}>
            {response.answer.limitations.map((limitation) => (
              <li key={limitation} className={styles.responseLimitation}>
                {limitation}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
